import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Calendar, User, CheckSquare, Archive } from 'lucide-react';
import { CardDialog } from './CardDialog';
import { CalendarCardDialog } from '@/components/CalendarCardDialog';
import { CircularProgress, ProgressState } from './CircularProgress';
import { CardContextMenu } from './CardContextMenu';
import { OptimizedCardPresenceAvatars } from './OptimizedCardPresenceAvatars';
import { FieldLockIndicator } from '@/components/collaboration/FieldLockIndicator';
import { useBoardContext } from '@/contexts/BoardContext';
import { useChecklistItems } from '@/hooks/useChecklistItems';
import { useBoardPresence } from '@/contexts/BoardPresenceContext';
import { useViewportCardTracking } from '@/hooks/useViewportCardTracking';
import { useEditingLocks } from '@/hooks/useEditingLocks';
import { useActivityStream } from '@/hooks/useActivityStream';
import { useAuth } from '@/hooks/useAuth';
import { useDragStateContext } from '@/contexts/DragStateContext';
import { useRemoteCardAnimation } from '@/hooks/useRemoteCardAnimation';
import { calculateEnhancedChecklistStats } from '@/utils/enhancedChecklistUtils';
import type { Tables } from '@/integrations/supabase/types';
import { cn } from '@/lib/utils';

type CardType = Tables<'cards'> & { assigned_profile?: Tables<'profiles'> };

interface KanbanCardProps {
  card: CardType;
  onRemoteUpdate?: (updateFn: () => void) => void;
}

export const KanbanCard = ({ card, onRemoteUpdate }: KanbanCardProps) => {
  const { tagDefinitions, updateCard, getTagName, getTagColor, canEditCards, currentBoardId } = useBoardContext();
  const { user } = useAuth();
  const { logActivity } = useActivityStream({ boardId: currentBoardId || '' });
  const [editOpen, setEditOpen] = useState(false);

  // **PHASE 2: Stabilized refs for drag state tracking**
  const currentBoardIdRef = useRef(currentBoardId);
  const userRef = useRef(user);
  
  // Update refs when values change
  useEffect(() => {
    currentBoardIdRef.current = currentBoardId;
  }, [currentBoardId]);
  
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Enhanced card presence tracking with new board-level system
  const { setUserStatus, getEditingUsersOnCard } = useBoardPresence();
  const editingUsers = getEditingUsersOnCard(card.id);

  // Get drag state to determine if this card is being dragged by someone else
  const { dragState } = useDragStateContext();
  const boardDragState = dragState[currentBoardId || ''] || [];
  const isBeingDraggedByOther = boardDragState.some(
    drag => drag.card_id === card.id && drag.user_id !== user?.id && drag.is_active
  );
  const isBeingDraggedByLocal = boardDragState.some(
    drag => drag.card_id === card.id && drag.user_id === user?.id && drag.is_active
  );

  // **PHASE 2: Stabilized animation setup with better dependency management**
  const { elementRef: animationRef, animateToNewPosition, cancelAnimation } = useRemoteCardAnimation(card.id, {
    duration: 300
  });

  // **PHASE 2: Stabilized animation trigger callback**
  const handleAnimationTrigger = useCallback(() => {
    console.log('🎬 [CARD] Triggering remote animation for card:', card.id);
    animateToNewPosition(() => {
      // The actual DOM update will happen through the normal React flow
      // The animation will be triggered by the position change
    });
  }, [card.id, animateToNewPosition]);

  // Viewport tracking for optimized presence updates
  const cardRef = useViewportCardTracking({
    cardId: card.id,
    threshold: 0.1
  });

  // Combine refs for both viewport tracking and animation
  const setRefs = useCallback((element: HTMLDivElement | null) => {
    if (cardRef.current !== element) {
      cardRef.current = element;
    }
    if (animationRef.current !== element) {
      animationRef.current = element;
    }
  }, [cardRef, animationRef]);

  // **PHASE 2: Stabilized remote update registration with proper cleanup**
  useEffect(() => {
    if (onRemoteUpdate) {
      const cleanup = onRemoteUpdate(handleAnimationTrigger);
      return cleanup;
    }
  }, [onRemoteUpdate, handleAnimationTrigger]);

  // **PHASE 2: Stabilized drag cancellation effect**
  useEffect(() => {
    if (isBeingDraggedByLocal) {
      cancelAnimation();
    }
  }, [isBeingDraggedByLocal, cancelAnimation]);

  // Editing locks for the card
  const { isFieldLocked, acquireLock, releaseLock } = useEditingLocks({
    entityType: 'card',
    entityId: card.id,
    enabled: true
  });

  // Get checklist items from database
  const { checklistItems } = useChecklistItems(card.id);
  
  // Get checklist metadata from card's checklists field
  const checklistMetadata = (card.checklists as { id: string; name: string }[]) || [];
  
  // Calculate stats with metadata
  const checklistStats = calculateEnhancedChecklistStats(checklistItems, checklistMetadata);
  const hasChecklists = checklistStats.totalItems > 0;

  // **PHASE 2: Stabilized user status effect**
  useEffect(() => {
    if (editOpen) {
      setUserStatus("editing", card.id);
    } else {
      setUserStatus("viewing", card.id);
    }
  }, [editOpen, card.id, setUserStatus]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString();
  };

  // Handle progress state change from clicking the circular indicator
  const handleProgressChange = async (newState: ProgressState) => {
    // Only allow progress changes if user can edit cards
    if (!canEditCards) return;
    
    // Try to acquire lock
    const lockAcquired = await acquireLock('progress_state');
    if (!lockAcquired) return;

    updateCard({
      cardId: card.id,
      updates: {
        progress_state: newState,
      },
    });

    // Log activity
    logActivity('card_updated', 'card', card.id, {
      title: card.title,
      field: 'progress_state',
      old_value: card.progress_state || 'not_started',
      new_value: newState
    });

    // Release lock after a delay
    setTimeout(() => releaseLock('progress_state'), 1000);
  };

  // Handle card click - but not if clicking on progress indicator
  const handleCardClick = async (event: React.MouseEvent) => {
    // Don't open edit dialog if clicking on the progress indicator
    if ((event.target as HTMLElement).closest('[data-progress-indicator]')) {
      return;
    }

    // Log viewing activity
    logActivity('card_viewed', 'card', card.id, { title: card.title });
    
    setEditOpen(true);
  };

  // Get the current progress state with fallback
  const currentProgressState = (card as any).progress_state || 'not_started';

  // Add assigned profile data to the card for CalendarCardDialog
  const cardWithProfile = {
    ...card,
    assigned_profile: card.assigned_profile
  };

  const cardContent = (
    <Card 
      ref={setRefs}
      className={cn(
        "kanban-card hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-200 dark:border-slate-600 hover:scale-[1.02] active:scale-100",
        card.archived 
          ? "bg-gray-100 dark:bg-slate-800 opacity-75 border-dashed" 
          : "bg-white dark:bg-slate-700"
      )}
      onClick={handleCardClick}
    >
      <CardHeader className="pb-2">
        {/* Presence avatars and archived indicator */}
        <div className="flex items-center justify-between mb-2">
          {card.archived && (
            <div className="flex items-center gap-1">
              <Archive className="h-3 w-3 text-gray-500 dark:text-slate-400" />
              <Badge variant="secondary" className="text-xs bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-slate-300">
                Archived
              </Badge>
            </div>
          )}
          {/* Show editing avatars for others actively editing */}
          <div className="ml-auto">
            {editingUsers.length > 0 && (
              <div className="flex items-center gap-1">
                {/* Show up to 2 editing avatars */}
                {editingUsers.slice(0,2).map(userPresence => (
                  <Avatar key={userPresence.user_id} className="h-6 w-6 border-2 border-green-500 shadow">
                    <AvatarFallback className="bg-green-500 text-white text-xs font-bold">
                      {(userPresence.user_name ?? "?").slice(0,2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                ))}
                {editingUsers.length > 2 && (
                  <span className="text-xs ml-1 text-green-700 dark:text-green-300 font-bold">
                    +{editingUsers.length - 2}
                  </span>
                )}
                <Badge variant="secondary" className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 animate-pulse ml-2">
                  {editingUsers.length === 1 ? "Editing" : `${editingUsers.length} editing`}
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Tags with lock protection */}
        {card.tags && card.tags.length > 0 && (
          <FieldLockIndicator entityType="card" entityId={card.id} fieldName="tags">
            <div className="flex flex-wrap gap-1">
              {card.tags.map((tag, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="text-xs"
                  style={{
                    backgroundColor: getTagColor(tag),
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {getTagName(tag)}
                </Badge>
              ))}
            </div>
          </FieldLockIndicator>
        )}
        
        <div className="flex items-start justify-between gap-2">
          {/* Title with lock protection */}
          <FieldLockIndicator entityType="card" entityId={card.id} fieldName="title">
            <h4 className={cn(
              "font-medium leading-tight flex-1",
              card.archived 
                ? "text-gray-500 dark:text-slate-400 line-through" 
                : "text-gray-900 dark:text-slate-100"
            )}>
              {card.title}
            </h4>
          </FieldLockIndicator>
          
          {/* Progress indicator with lock protection */}
          <FieldLockIndicator entityType="card" entityId={card.id} fieldName="progress_state">
            <div data-progress-indicator className="flex-shrink-0">
              <CircularProgress
                state={currentProgressState as ProgressState}
                onStateChange={handleProgressChange}
                size="sm"
                disabled={!canEditCards || card.archived || isFieldLocked('progress_state')}
              />
            </div>
          </FieldLockIndicator>
        </div>
        
        {/* Description with lock protection */}
        {card.description && (
          <FieldLockIndicator entityType="card" entityId={card.id} fieldName="description">
            <p className={cn(
              "text-sm line-clamp-2",
              card.archived 
                ? "text-gray-400 dark:text-slate-500" 
                : "text-gray-600 dark:text-slate-300"
            )}>
              {card.description}
            </p>
          </FieldLockIndicator>
        )}
      </CardHeader>
      
      <CardContent className="pt-0 space-y-3">
        {/* Checklist Progress Bar */}
        {hasChecklists && (
          <div className="space-y-1">
            <Progress 
              value={checklistStats.completionPercentage} 
              className="h-1.5"
            />
          </div>
        )}

        {/* Dates */}
        {(card.start_date || card.end_date) && (
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
            <Calendar className="h-3 w-3 text-gray-600 dark:text-slate-300" />
            <span>
              {card.start_date && formatDate(card.start_date)}
              {card.start_date && card.end_date && ' - '}
              {card.end_date && formatDate(card.end_date)}
            </span>
          </div>
        )}

        {/* Category */}
        {card.category && (
          <Badge variant="outline" className="text-xs w-fit border-gray-300 dark:border-slate-500 text-gray-700 dark:text-slate-300">
            {card.category}
          </Badge>
        )}

        {/* Assigned user */}
        {card.assigned_profile && (
          <div className="flex items-center gap-2">
            <Avatar className="h-6 w-6">
              <AvatarFallback className="text-xs bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-slate-300">
                {card.assigned_profile.full_name?.charAt(0) || 
                 card.assigned_profile.username?.charAt(0) || 
                 '?'}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs text-gray-600 dark:text-slate-300">
              {card.assigned_profile.full_name || card.assigned_profile.username}
            </span>
          </div>
        )}

        {/* Enhanced Checklist Indicators */}
        {hasChecklists && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400">
                <CheckSquare className="h-3 w-3 text-gray-600 dark:text-slate-300" />
                <span>
                  {checklistStats.completedItems}/{checklistStats.totalItems} tasks
                </span>
                <Badge variant="secondary" className="text-xs">
                  {checklistStats.completionPercentage}%
                </Badge>
              </div>
              
              {/* Visual indicators for overdue/high priority items */}
              <div className="flex items-center gap-1">
                {checklistStats.hasOverdueItems && (
                  <div className="h-3 w-3 text-red-500" />
                )}
                {checklistStats.hasHighPriorityItems && (
                  <div className="h-2 w-2 rounded-full bg-red-500" />
                )}
              </div>
            </div>
            
            {/* Show checklist names from metadata */}
            {checklistStats.checklistNames.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400">
                <CheckSquare className="h-3 w-3" />
                <span className="truncate">
                  {checklistStats.checklistNames.slice(0, 2).join(', ')}
                  {checklistStats.checklistNames.length > 2 && ` +${checklistStats.checklistNames.length - 2}`}
                </span>
              </div>
            )}
            
            {/* Assigned users for checklist items */}
            {checklistStats.assignedUsers.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400">
                <User className="h-3 w-3" />
                <span className="truncate">
                  {checklistStats.assignedUsers.slice(0, 2).join(', ')}
                  {checklistStats.assignedUsers.length > 2 && ` +${checklistStats.assignedUsers.length - 2}`}
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Under the title: add a badge if current user can't edit */}
      {!canEditCards && (
        <Badge 
          variant="secondary" 
          className="mb-2 text-xs bg-gray-100 text-gray-600"
          title="You are viewing (read-only)"
        >
          Viewing
        </Badge>
      )}
    </Card>
  );

  return (
    <>
      <CardContextMenu card={card} onEdit={() => setEditOpen(true)}>
        {cardContent}
      </CardContextMenu>

      {/* Use CardDialog for editors, CalendarCardDialog for viewers */}
      {canEditCards ? (
        <CardDialog
          card={card}
          open={editOpen}
          onOpenChange={setEditOpen}
          isCreateMode={false}
        />
      ) : (
        <CalendarCardDialog
          card={cardWithProfile}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
    </>
  );
};
